import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Typography } from "@mui/material";
import { renderIcon } from "../../../helpers/renderDynamicIcons";

/**
 * InfoStackPanel
 *
 * Generic stacked details panel.
 * Renders a list of stacked cards (label/value).
 * Intended to be driven by DB json/jsonb (e.g. column `service_details_data`).
 *
 * ------------------------------------------------------------------
 * ✅ Expected `items` prop / DB json shape (service_details_data)
 * ------------------------------------------------------------------
 * items = [
 *   {
 *     id: "fees",                     // optional but recommended (string) stable key
 *     label: "Service fees",          // optional (string)
 *     value: "Free"                   // recommended (string/number)
 *     icon: "AttachMoney"             // optional (string: MUI icon name | React element | component)
 *   },
 *   {
 *     id: "duration",
 *     label: "Completion time",
 *     value: "Immediate"
 *   }
 * ]
 *
 */
const InfoStackPanel = ({
  title = "",
  subtitle = "",
  items = [],
  containerSx = {},
  headerSx = {},
  itemSx = {},
}) => {
  const [t] = useTranslation();
  const safeItems = useMemo(() => items || [], [items]);

  if (!safeItems.length) return null;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: "background.paper",
        ...containerSx,
      }}
    >
      {(title || subtitle) && (
        <Box sx={{ mb: 2, ...headerSx }}>
          {!!title && (
            <Typography variant="body2" color="primary" fontSize={20}>
              {t(title)}
            </Typography>
          )}

          {!!subtitle && (
            <Typography variant="h6" sx={{ fontWeight: 700, mt: title ? 0.25 : 0 }}>
              {t(subtitle)}
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {safeItems.map((it, idx) => {
          const key = it?.id || `${idx}`;

          return (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "background.paper",
                ...itemSx,
              }}
            >
              {!!it?.label && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t(it.label)}
                </Typography>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {it?.icon ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {renderIcon(it.icon, { sx: { color: "#1f7a54", fontSize: 26 } })}
                  </Box>
                ) : null}

                <Typography variant="body1" sx={{ fontSize: 14  }}>
                  {t(it?.value ?? "")}
                </Typography>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
};

export default memo(InfoStackPanel);
